import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Eye, RadioTower } from "lucide-react";
import { useTableSort, SortTh } from "@/lib/tableSort";
import { InfraFilters } from "@/pages/NetworkOdc";
import { ServerFilter } from "@/components/network/ServerFilter";
import { OltFormDialog } from "@/components/network/OltFormDialog";
import { OltDetail } from "@/components/network/OltDetail";
import { StatusPill, LocCell } from "@/components/network/StatusPill";
import { ArahkanButton } from "@/components/network/ArahkanButton";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { useNetworkRefs, SELECT_CLS, OLT_TECH } from "@/lib/network";
import { OldKodeLabel } from "@/components/network/OldKodeLabel";

export default function NetworkOlt() {
  const { refs, wilayahName, lokasiName } = useNetworkRefs();
  const [f, setF] = useState({ q: "", lokasi_id: "", wilayah_id: "", status: "", teknologi: "", server_id: "" });
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get("/network/olt", { params: f }).then((r) => setRows(r.data)), [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const getters = useMemo(() => ({ kode: (o) => o.kode, nama: (o) => o.nama, model: (o) => `${o.vendor} ${o.model}`, lokasi: (o) => lokasiName(o.lokasi_id),
    wilayah: (o) => wilayahName(o.wilayah_id), port: (o) => o.port_count, terpakai: (o) => o.port_terpakai, status: (o) => o.status }), [wilayahName, lokasiName]);
  const st = useTableSort("olt", rows, getters);
  const canManage = !!refs?.me?.can_manage;
  const cols = [["kode", "Kode"], ["nama", "Nama"], ["model", "Vendor/Model"], ["lokasi", "Lokasi"], ["wilayah", "Wilayah"], ["port", "Port PON"], ["terpakai", "Terpakai"], ["status", "Status"]];
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="network-olt-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Master OLT</h1>
          <p className="text-sm text-[#6B7280]">Inventaris OLT, port PON, dan topologi sampai pelanggan{canManage ? "" : " (mode lihat)"}</p></div>
        {canManage && <Button onClick={() => setForm({})} data-testid="olt-add-btn"><Plus className="mr-1 h-4 w-4" />Tambah OLT</Button>}
      </div>
      <InfraFilters refs={refs} f={f} setF={setF} prefix="olt" placeholder="Cari kode, nama, model, serial…">
        <select className={SELECT_CLS} value={f.teknologi} onChange={(e) => setF((x) => ({ ...x, teknologi: e.target.value }))} data-testid="olt-filter-teknologi">
          <option value="">Semua Teknologi</option>{OLT_TECH.map((t) => <option key={t}>{t}</option>)}</select>
        <ServerFilter f={f} setF={setF} prefix="olt" />
      </InfraFilters>
      <div className="overflow-x-auto rounded-lg border border-[#E5E7EB] bg-white">
        <table className="w-full min-w-[900px] text-sm" data-testid="olt-table">
          <thead className="bg-[#F8F9FA] text-left text-xs uppercase text-[#6B7280]">
            <tr>{cols.map(([c, l], i) => <SortTh key={c} className={i ? "" : "p-2"} col={c} label={l} s={st} testid="olt" />)}<th>Peta</th><th className="pr-2 text-right">Aksi</th></tr>
          </thead>
          <tbody>
            {(st.sorted || []).map((o) => (
              <tr key={o.id} className="border-t" data-testid={`olt-row-${o.kode}`}>
                <td className="p-2 font-mono-tab font-semibold">{o.kode}<OldKodeLabel row={o} q={f.q} testid={`olt-oldkode-${o.kode}`} /></td><td>{o.nama}</td><td>{o.vendor} {o.model}<div className="text-[11px] text-[#6B7280]">{o.teknologi}</div></td>
                <td>{lokasiName(o.lokasi_id)}</td><td className={o.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(o.wilayah_id)}</td>
                <td>{o.port_count}</td><td data-testid={`olt-used-${o.kode}`}>{o.port_terpakai}</td><td><StatusPill status={o.status} /></td>
                <td><LocCell row={o} testid={`olt-nocoord-${o.kode}`} /></td>
                <td className="whitespace-nowrap pr-2 text-right">
                  <ArahkanButton iconOnly lat={o.latitude} lng={o.longitude} testid={`olt-nav-${o.kode}`} />
                  <Button size="icon" variant="ghost" onClick={() => setDetail(o.id)} data-testid={`olt-view-${o.kode}`}><Eye className="h-4 w-4" /></Button>
                  {canManage && <Button size="icon" variant="ghost" onClick={() => setForm(o)} data-testid={`olt-edit-${o.kode}`}><Pencil className="h-4 w-4" /></Button>}
                  <DeleteInfraButton iconOnly kind="olt" item={o} me={refs?.me} onDeleted={load} />
                </td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={10} className="p-8 text-center text-[#6B7280]" data-testid="olt-empty">
              <RadioTower className="mx-auto mb-2 h-8 w-8 opacity-40" />Belum ada OLT{Object.values(f).some(Boolean) ? " untuk filter ini" : ""}.
              {canManage && !Object.values(f).some(Boolean) && " Klik Tambah OLT untuk mendata perangkat pertama."}</td></tr>}
          </tbody>
        </table>
      </div>
      <OltFormDialog open={!!form} editing={form?.id ? form : null} refs={refs} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[calc(100dvh-5.5rem)] max-w-3xl overflow-y-auto overflow-x-hidden max-lg:top-[calc(50%+2rem)] lg:max-h-[92vh]" data-testid="olt-detail-dialog">
          <DialogHeader><DialogTitle>Detail OLT</DialogTitle></DialogHeader>
          {detail && <OltDetail oltId={detail} canManage={canManage} me={refs?.me} wilayahName={wilayahName} lokasiName={lokasiName} onChanged={load}
                                onDeleted={() => { setDetail(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
